import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useMemo } from "react";
import {
  MagnifyingGlass,
  Percent,
  Gift,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getLocalizedText } from "@shared/lib/utils";
import { useAppHeader } from "@shared/components/AppHeaderContext";

export const Route = createFileRoute("/app/offers/")({
  component: OffersListPage,
});

function OffersListPage() {
  const currentLang = getCurrentLanguage();
  const {
    offersSearchQuery: searchQuery,
    setOffersSearchQuery: setSearchQuery,
    offersSelectedCategory: selectedCategory,
    setOffersSelectedCategory: setSelectedCategory,
    offersSortBy: sortBy,
    setOffersSortBy: setSortBy,
    offersViewMode: viewMode,
  } = useAppHeader();

  const offers = useQuery(api.offers.getActiveOffers, {});

  // Filtrare și sortare
  const filteredAndSortedOffers = useMemo(() => {
    if (!offers) return [];

    let filtered = offers.filter((offer) => {
      // Filtrare pe categorie
      if (selectedCategory && offer.category?.slug !== selectedCategory) {
        return false;
      }

      // Filtrare pe search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const partnerName = offer.partner?.name?.toLowerCase() || "";
        const description = getLocalizedText(offer.description, currentLang).toLowerCase();
        const shortDesc = getLocalizedText(offer.shortDescription, currentLang).toLowerCase();
        const categoryName = offer.category?.name?.toLowerCase() || "";

        if (
          !partnerName.includes(query) &&
          !description.includes(query) &&
          !shortDesc.includes(query) &&
          !categoryName.includes(query)
        ) {
          return false;
        }
      }

      return true;
    });

    // Sortare
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "discount":
          const discountA = a.discountType === "percent" ? (a.discountValue || 0) : 0;
          const discountB = b.discountType === "percent" ? (b.discountValue || 0) : 0;
          return discountB - discountA;
        case "popularity":
          return (b.totalRedemptions || 0) - (a.totalRedemptions || 0);
        case "distance":
          // TODO: Implementare sortare pe distanta daca avem locatia userului
          return 0;
        default:
          return 0;
      }
    });

    return filtered;
  }, [offers, selectedCategory, searchQuery, sortBy, currentLang]);

  if (offers === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="px-4 pt-[140px] pb-24">
        {filteredAndSortedOffers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlass size={24} className="text-text-muted" />
            </div>
            <h5 className="text-lg font-semibold text-text mb-2">
              {currentLang === "ro" ? "Nu s-au găsit oferte" : "No offers found"}
            </h5>
            <p className="text-text-muted mb-6">
              {currentLang === "ro"
                ? "Încearcă să modifici filtrele sau termenii de căutare"
                : "Try adjusting your filters or search terms"}
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory(null);
                setSortBy("discount");
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold"
            >
              {currentLang === "ro" ? "Resetează filtrele" : "Reset filters"}
            </button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredAndSortedOffers.map((offer) => (
              <OfferCard
                key={offer._id}
                offer={offer}
                currentLang={currentLang}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3 pb-10">
            {filteredAndSortedOffers.map((offer) => (
              <OfferCard
                key={offer._id}
                offer={offer}
                currentLang={currentLang}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const OfferCard = React.memo(function OfferCard({
  offer,
  currentLang,
}: {
  offer: any;
  currentLang: "ro" | "en";
}) {
  const description = useMemo(
    () => getLocalizedText(offer.shortDescription || offer.description, currentLang),
    [offer.shortDescription, offer.description, currentLang]
  );
  const benefitText = useMemo(
    () => getLocalizedText(offer.benefitText, currentLang),
    [offer.benefitText, currentLang]
  );

  const discount = offer.discountType === "percent"
    ? `${offer.discountValue}%`
    : benefitText;

  return (
    <div className="mb-3">
      <Link
        to="/app/offers/$offerId"
        params={{ offerId: offer._id }}
        className="flex items-center gap-4"
      >
        <div className="w-24 h-24 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
          {offer.partner?.carouselImageUrl ? (
            <img
              src={offer.partner.carouselImageUrl}
              alt={offer.partner.name}
              className="w-full h-full object-cover"
            />
          ) : offer.discountType === "percent" ? (
            <Percent size={20} className="text-primary" />
          ) : (
            <Gift size={20} className="text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-text truncate">
            {offer.partner?.name}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {offer.category?.name} • {discount}
          </p>
          <span className="inline-block px-2 py-1 bg-primary/5 text-primary text-xs font-bold rounded mt-2 whitespace-pre-line break-words">
            {description}
          </span>
        </div>
      </Link>
    </div>
  );
});
